/**
 * Strings de interface centralizadas (Apendice H.2 e H.12).
 * Linguagem de negocio em portugues do Brasil. Termos internos como tenant,
 * provider, webhook, tool, runtime, token, payload e companyId nunca aparecem.
 * Centralizado para permitir internacionalizacao futura.
 */
/**
 * RIM-189 - "24 horas" e mudo; "1 dia" e uma duracao que alguem sente.
 *
 * Horas quebradas continuam em horas: "1 dia e 4 horas" e pior de ler que
 * "28 horas" para um prazo operacional.
 */
function horasEmTexto(horas: number): string {
  if (horas % 24 !== 0) return horas === 1 ? '1 hora' : `${horas} horas`;
  const dias = horas / 24;
  return dias === 1 ? '1 dia' : `${dias} dias`;
}

export const t = {
  brand: {
    name: 'Rimati',
    tagline: 'Transforme conversas em clientes.',
  },

  nav: {
    home: 'Início',
    conversations: 'Conversas',
    contacts: 'Clientes',
    opportunities: 'Oportunidades',
    calendar: 'Agenda',
    agents: 'Assistentes',
    automations: 'Automações',
    results: 'Resultados do atendimento',
    settings: 'Configurações',
    /* R058 - quem opera a plataforma ve as clinicas; quem e clinica nunca ve isto. */
    platform: 'Plataforma',
    supportQueue: 'Fila de suporte',
    incidents: 'Incidentes',
    /** RIM-176 - quinto item da navegação inferior (v0.2, seção 10.2). */
    more: 'Mais',
    moreTitle: 'Mais no Rimati',
    closeMore: 'Fechar',
    openMenu: 'Abrir menu',
    primary: 'Navegação principal',
    /*
      RIM-192 - o indicador de demonstracao (secao 14).

      Quem esta vendo precisa saber que os dados sao de mentira, e quem opera
      precisa saber que NAO esta na conta da clinica. Sem isso, alguem responde
      uma conversa de demonstracao achando que esta falando com um cliente.
    */
    demo: 'Demonstração',
    demoHint: 'Ambiente de demonstração. Nenhum cliente real recebe estas mensagens.',
  },

  login: {
    title: 'Entrar no Rimati',
    /*
      RIM-177 - a primeira tela diz o que o produto FAZ.

      "Acesse a operacao de atendimento da sua empresa" descreve o ato de
      entrar, nao o produto. E a unica tela que alguem ve antes de conhecer o
      Rimati; gastar essa linha explicando o botao ao lado e desperdicio.
    */
    subtitle: 'Converse com o Rimati e cuide da sua empresa em um só lugar.',
    email: 'E-mail',
    password: 'Senha',
    submit: 'Entrar',
    submitting: 'Entrando...',
  },

  home: {
    /* Secao 4 - o nome do ✦ para quem nao ve o simbolo. */
    byRimati: 'Feito pelo Rimati',
    title: 'Início',
    subtitle: 'O que chegou, o que o Rimati fez e onde há oportunidade.',

    /*
      RIM-178 - o cabecalho diz RESULTADO, nao o que a tela e.
      "O que chegou, o que o Rimati fez e onde ha oportunidade" descreve o
      menu. Quem abre o Inicio de manha quer saber o que aconteceu.

      Cada frase abaixo so e usada quando os numeros a sustentam. Nenhuma
      inventa: todas saem de contagem real do periodo.
    */
    greetingMorning: 'Bom dia',
    greetingAfternoon: 'Boa tarde',
    greetingEvening: 'Boa noite',
    /** Ainda nao houve nada no periodo. Dizer isso e melhor que forcar euforia. */
    resultEmpty: 'Nada chegou nos últimos 30 dias ainda.',
    resultContacts: (pessoas: number) =>
      pessoas === 1 ? '1 pessoa nova chegou' : `${pessoas} pessoas novas chegaram`,
    resultAppointments: (consultas: number, doAgente: number) => {
      const base = consultas === 1 ? '1 atendimento marcado' : `${consultas} atendimentos marcados`;
      if (doAgente === 0) return base;
      // Parenteses, e nao virgula: a frase inteira ja usa virgula para separar
      // os itens da lista, e "15 consultas marcadas, 1 pelo Rimati" fazia o
      // detalhe parecer um terceiro item.
      return doAgente === consultas
        ? `${base} (todas pelo Rimati)`
        : `${base} (${doAgente} pelo Rimati)`;
    },
    resultPipeline: (valor: string) => `${valor} em oportunidades abertas`,
    resultPeriod: 'nos últimos 30 dias',
    metrics: {
      contacts: 'Pessoas novas',
      opportunities: 'Oportunidades criadas',
      appointments: 'Consultas marcadas',
      /*
        "Valor em aberto" pode ser lido como divida, conta a receber ou
        inadimplencia — o oposto do que o numero significa. "Em oportunidades"
        diz de onde ele vem.
      */
      pipelineValue: 'Valor em oportunidades',
      pipelineHint: 'Todas as oportunidades abertas',
    },
    working: 'Rimati trabalhando',
    /*
      R012 - esta cadeia EXISTIA e nao estava ligada a nada.

      Alguem escreveu o rotulo e o controle nunca foi montado: o Inicio mostrava
      dez linhas e nao havia caminho para o resto. Uma cadeia de texto orfa e a
      pegada de uma intencao que nao virou produto — e ela passa em toda revisao,
      porque nenhum compilador reclama de um rotulo que ninguem usa.
    */
    workingSeeAll: 'Ver toda a atividade',
    /*
      HOME WOW - as frases do painel "Rimati trabalhando".

      Todas comecam com "Rimati" e um VERBO: e o que transforma um registro de
      sistema na sensacao de que existe alguem trabalhando. Nenhuma usa palavra
      de CRM — nem etapa, nem funil, nem pipeline.
    */
    actContact: (nome: string) => `Rimati cadastrou ${nome}`,
    actConversation: (nome: string) => `Rimati começou a atender ${nome}`,
    actInterest: (nome: string) => `Rimati identificou o que ${nome} procura`,
    actOpportunity: (nome: string) => `Rimati criou uma oportunidade para ${nome}`,
    actStage: (nome: string, etapa: string) => `Rimati avançou ${nome} para ${etapa}`,
    actAppointment: (nome: string) => `Rimati marcou o atendimento de ${nome}`,
    actFollowUp: (nome: string) => `Rimati programou um acompanhamento para ${nome}`,
    actHandoff: (nome: string) => `Rimati chamou a equipe para atender ${nome}`,
    actWon: (nome: string) => `Rimati registrou o fechamento de ${nome}`,
    /*
      R012 - o Inicio mostra as ultimas; as COMPLETAS ficam a um clique.

      O criterio pede que "atividades completas fiquem acessiveis". Dez linhas
      no Inicio e a hierarquia certa — quem chega quer decidir, nao ler tudo —,
      mas sem caminho para o resto a informacao simplesmente nao existia para
      quem precisasse dela.
    */
    workingSeeLess: 'Ver menos',
    workingEmpty: 'Nada aconteceu ainda hoje. Envie uma mensagem de teste para ver o Rimati agir.',
    simulator: 'Simulador de cliente',
    simulatorHelp: 'Envie uma mensagem como se fosse um cliente.',
    /*
      R012 - o simulador nasce RECOLHIDO.

      Ele e ferramenta de demonstracao, e ocupava a coluna esquerda do Inicio
      com um formulario inteiro — competindo por espaco com as pendencias, que
      sao o motivo de a tela existir. Recolhido, ele continua identificado e
      deixa de empurrar a decisao para baixo da dobra.
    */
    simulatorOpen: 'Abrir simulador',
    simulatorClose: 'Recolher',
    /** RIM-179 - o gráfico principal do Início. */
    /** HOME WOW: a conversao vive junto do grafico que a explica. */
    conversion: 'Conversão para atendimento',
    conversionDetail: (consultas: number, pessoas: number) =>
      `${consultas} de ${pessoas} ${pessoas === 1 ? 'pessoa marcou' : 'pessoas marcaram'} atendimento`,
    chartTitle: 'Pessoas novas e atendimentos marcados',
    chartHelp: 'Como o período evoluiu, dia a dia.',
    chartContacts: 'Pessoas novas',
    chartAppointments: 'Consultas marcadas',
    chartDay: 'Dia',
    radar: 'Oportunidades que precisam de atenção',
    radarHelp: 'Oportunidades abertas que pararam de andar.',
    radarEmpty: 'Nenhuma oportunidade parada. Tudo em dia.',
    /** RIM-180 - o resumo em cima do Radar. */
    radarStalled: (valor: string) => `${valor} parados`,
    radarCount: (n: number) =>
      n === 1 ? '1 oportunidade precisa de atenção' : `${n} oportunidades precisam de atenção`,
    radarSeeAll: 'Ver oportunidades',
    radarStalledHint: 'Valor estimado das oportunidades abertas que não estão andando.',
    radarFilterHint: 'Ver estas oportunidades',
    radarMore: (rest: number) =>
      rest === 1 ? 'Mais 1 oportunidade parada' : `Mais ${rest} oportunidades paradas`,
    /*
      Curto de proposito. "que no periodo anterior" repete em quatro cartoes a
      mesma informacao que o seletor de periodo ja da, e em 360px empurrava o
      texto para duas linhas em todos eles.
    */
    trendUp: (percent: number) => `↑ ${percent}% no período`,
    trendDown: (percent: number) => `↓ ${percent}% no período`,
    trendFlat: 'Estável no período',
    trendNew: 'Sem base de comparação',
  },
  results: {
    /*
      RESULTADOS WOW (secoes 9 e 10) - o grafico mostra EVENTOS, e o titulo diz
      qual e a unidade.

      "Receita fechada" prometia mais do que o dado sustenta: o produto guarda o
      valor ESTIMADO da oportunidade, e nao nota, contrato ou recebimento.
    */
    revenueChart: 'Valor convertido, dia a dia',
    revenueChartHelp: 'Somado pelo dia em que a oportunidade foi marcada como ganha.',
    revenueSeries: 'Valor convertido',
    title: 'Resultados do atendimento',
    subtitle: 'O que o atendimento gerou no período.',
    /* Secoes 13 a 19 - o funil conta PESSOAS, e cada degrau sai do anterior. */
    funnel: 'Como as pessoas avançaram',
    /*
      A CONVENCAO dos dois denominadores, dita uma vez.

      A linha "Com oportunidade · 9 · 39%" tem duas reguas: a porcentagem e sobre
      o degrau anterior e a barra e proporcional a coorte inicial. Ate aqui o
      unico esclarecimento vivia num atributo `title` — que nao existe em
      telefone nenhum, e o celular e onde a duvida aparecia.

      Repetir o denominador em cada linha ("39% de pessoas atendidas") tambem
      resolvia, e foi tentado: duplicava o nome de cada degrau na tela e
      estourava a linha nas larguras pequenas. Uma frase no cabecalho cobre as
      duas reguas sem repetir nada.
    */
    funnelHelp:
      'De quem a empresa atendeu no período, até onde cada pessoa chegou. Cada porcentagem é sobre o degrau anterior; as barras são proporcionais ao primeiro.',
    funnelRateHint: (anterior: string) => `do degrau "${anterior}"`,
    /*
      Secao 26 - o titulo tem de corresponder a regra.

      "Onde o dinheiro esta parado" prometia um filtro de estagnacao que nao
      existe: a lista mostra TODAS as oportunidades abertas por etapa. Oportunidade
      que entrou ontem e esta andando nao esta parada, e aparecia sob esse titulo.
    */
    stageValues: 'Valor por etapa',
    /* "Pipeline" é palavra de quem constrói o CRM, e não de quem usa (seção 5). */
    stageValuesHelp: 'Todas as oportunidades abertas agora, etapa por etapa.',
    stageValuesCta: 'Ver oportunidades',
    revenue: 'Fechamentos do período',
    /*
      Secao 2 - "Ganho" era ambiguo: podia ser lucro, faturamento, comissao ou
      margem. O que o numero e: a soma do valor estimado das oportunidades
      marcadas como ganhas no periodo. Nao e receita confirmada, porque nada no
      produto confirma recebimento — e por isso o rotulo fala em VALOR
      CONVERTIDO.
    */
    won: 'Valor convertido',
    wonHelp: 'Valor das oportunidades marcadas como ganhas no período.',
    lost: 'Perdido',
    /*
      Secoes 4 e 53 - o nome mais forte que o dado sustenta, e nem uma palavra
      alem.

      O produto prova a ORDEM: houve acompanhamento, e depois a oportunidade foi
      ganha. Nao prova a causa — entre a mensagem e a venda pode ter havido uma
      conversa inteira conduzida por uma pessoa. "Recuperado pelo Rimati" afirma
      autoria; "convertido apos acompanhamento" afirma o que aconteceu.
    */
    recovered: 'Convertido após acompanhamento',
    recoveredHelp:
      'Valor das oportunidades ganhas que receberam uma mensagem automática antes de fechar.',
    followUps: 'Mensagens de acompanhamento',
    followUpsHelp: 'Mensagens que o Rimati enviou sozinho para retomar conversas paradas.',
    followUpsSent: 'Enviadas',
    followUpsAnswered: 'Responderam',
    followUpsCancelled: 'Canceladas',
    followUpsWindow: 'Resposta conta quando o cliente escreve em até 7 dias.',
    empty: 'Ainda não há movimento suficiente neste período.',
    /*
      Secoes 6 e 7 - "Em aberto agora" podia ser lido como divida ou cobranca. E
      o rotulo escondia o principal: este numero NAO obedece ao periodo, porque
      e uma foto do pipeline de agora.
    */
    openValue: 'Valor em oportunidades',
    openValueHelp: 'Todas as oportunidades abertas agora, independente do período.',
    ofWon: (percent: number) => `${percent}% do valor convertido`,
    answeredRate: (percent: number) => `${percent}% responderam`,
  },

  /**
   * R109 - primeiros passos.
   *
   * Nenhuma frase aqui usa "onboarding", "setup" ou "wizard". Quem lê é a dona
   * de uma empresa, e o nome do processo é problema de quem escreve software.
   */
  onboarding: {
    title: 'Para o Rimati começar a atender',
    subtitle: 'Dá para parar e voltar quando quiser: nada se perde.',
    progress: (feitos: number, total: number) => `${feitos} de ${total} prontos`,
    go: 'Fazer agora',
    initialDone: 'Passos iniciais concluídos',
    /* Ditos só para leitor de tela: a cor e o traço não chegam a quem não vê. */
    done: 'Pronto:',
    pending: 'Falta:',
  },
  /**
   * R058 - a área do operador.
   *
   * Quem lê aqui opera a plataforma, e não uma empresa: "empresas" são clientes,
   * e "atendimentos" são o que elas fizeram. Ainda assim nada aqui usa jargão de
   * banco ou de código — o vocabulário é de operação, não de implementação.
   */
  plataforma: {
    title: 'Plataforma',
    subtitle: 'O que as empresas fizeram, e o que este painel ainda não mede.',
    dias: (n: number) => `${n} dias`,
    periodo: 'Período:',
    origem: 'De onde vem:',
    naoConta: 'Não conta:',
    medidasLabel: 'Indicadores da plataforma',
    naoMedidosLabel: 'O que não é medido',
    naoMedidosTitle: 'O que este painel NÃO mede',
    naoMedidosHint: 'Está escrito porque omitir criaria a impressão de que o número é zero.',
    empresasLabel: 'Empresas',
    empresasTitle: 'Por empresa',
    empresasHint: 'É o detalhe que torna os números de cima conferíveis.',
    colEmpresa: 'Empresa',
    colAtendimentos: 'Atendimentos',
    colExecucoes: 'Chamadas de IA',
    colPendencias: 'Precisam de gente',
    seloDemo: '· demonstração',
    ativacaoTitle: 'Ativação e retorno',
    ativacaoHint:
      'Eventos próprios, sem dado pessoal. Empresas de teste (TESTE P…) ficam de fora. Os três primeiros passos são indicativos: contam visitas, não pessoas, sem filtro de robôs (a mesma pessoa pode contar mais de uma vez; há teto diário). Os demais contam empresas cadastradas na janela.',
    ativacaoLabel: 'Funil de ativação',
    ativacaoPassoAnterior: (p: number) => `${String(p).replace('.', ',')}% do passo anterior`,
    ativacaoRetencaoTitle: 'Retorno com ação útil',
    ativacaoElegiveis: (n: number, total: number) => `${n} de ${total} empresas`,
    ativacaoSemDados: 'Sem dados na janela',
    ativacaoExcluidas: (n: number) => `${n} empresa(s) de teste excluída(s).`,
    verificadoEm: (iso: string, dias: number) =>
      `Consultado em ${new Date(iso).toLocaleString('pt-BR')}, sobre os últimos ${dias} dias.`,
  },
  /** R059 - o que a empresa vê sobre o que relatou. */
  relatos: {
    titulo: 'O que você relatou',
    vazio: 'Nada relatado ainda',
    vazioHint: 'Quando você relatar um problema, o andamento dele aparece aqui.',
    aberto: (data: string) => `Aberto em ${data}`,
    com: (nome: string) => `com ${nome}`,
  },

  /** R060 - a fila de quem atende as empresas. */
  fila: {
    title: 'Fila de suporte',
    subtitle: 'O que as empresas relataram, começando pelo que espera há mais tempo.',
    filtroLabel: 'Filtrar por estado',
    todos: 'Todos',
    vazio: 'Nenhum chamado',
    vazioHint: 'Nada aguardando resposta neste filtro.',
    agora: 'agora há pouco',
    horas: (n: number) => `há ${n}h`,
    dias: (n: number) => `há ${n} ${n === 1 ? 'dia' : 'dias'}`,
    diagnostico: 'Detalhes técnicos',
    tela: 'Tela:',
    navegador: 'Navegador:',
    /* Dito porque uma fila sem prazo parece uma fila em dia quando ninguém diz o contrário. */
    semPrazo: 'Não há prazo de resposta definido, então nada aqui está atrasado ou em dia.',
  },
  /** R061 e R063 - o que quebra em várias empresas, e o que isso sugere. */
  incidentes: {
    title: 'Incidentes',
    subtitle: 'Problemas que atingiram mais de uma empresa nos últimos dias.',
    listaLabel: 'Incidentes',
    vazio: 'Nenhum incidente',
    vazioHint: 'Nada falhou em mais de uma empresa no período.',
    /* "Parou" e não "resolvido": o servidor observa ausência, e ausência não é conserto. */
    parou: (h: number) => (h < 1 ? 'parou agora há pouco' : `parou há ${h}h`),
    acontecendo: 'acontecendo',
    alcance: (empresas: number, vezes: number) =>
      `${empresas} ${empresas === 1 ? 'empresa' : 'empresas'} · ${vezes} ${vezes === 1 ? 'vez' : 'vezes'}`,
    onde: {
      atendimento: 'no atendimento',
      envio: 'no envio',
      canal: 'no canal',
    },
    sugestoesLabel: 'Sugestões',
    sugestoesTitle: 'O que isto sugere',
    sugestoesHint: 'Cada sugestão vem com a evidência e com o que ela não prova.',
    semSugestao: 'Sem sugestões no período.',
    naoProva: 'O que isto NÃO prova:',
    evidencia: 'Em cima de quê:',
    amostra: (n: number) => `Amostra: ${n} ${n === 1 ? 'empresa' : 'empresas'}.`,
  },
  conversations: {
    /*
      R103 - o aviso de que chegou algo enquanto a pessoa lia mais acima.

      "Ver mensagem nova" e nao "Ir para o fim": o que interessa nao e a posicao,
      e o fato de que a conversa andou sem ela.
    */
    newMessages: 'Ver mensagem nova',
    thread: 'Mensagens da conversa',
    title: 'Conversas',
    subtitle: 'Atendimentos em andamento e o contexto de cada cliente.',
    listEmpty: 'Nenhuma conversa por aqui ainda.',
    listEmptyHint: 'Quando um cliente enviar mensagem, a conversa aparece nesta lista.',
    pickOne: 'Escolha uma conversa',
    pickOneHint: 'Selecione um atendimento na lista para ver as mensagens e o contexto.',
    replying: 'Rimati está respondendo...',
    // O distintivo divide a barra com o nome do cliente e duas ações. Em
    // 1280 a frase inteira empurrava o nome para "Bruno ..." — ela virou dica.
    /*
      RIM-182 - o topo diz QUEM está atendendo, com nome (seção 11.3).

      O selo dizia "Rimati" enquanto a mensagem logo abaixo vinha assinada
      "Lívia": dois nomes para a mesma coisa na mesma tela. Rimati é o produto;
      Lívia é a assistente da empresa, e é ela quem está na conversa.
    */
    modeAi: (assistente: string) => `✦ ${assistente} está atendendo`,
    modeAiFull: (assistente: string) => `${assistente} está atendendo esta conversa`,
    /*
      ACEITAÇÃO FINAL (secao 6) - autoria exata, e nunca inventada.

      Sao tres frases porque sao tres situacoes diferentes, e a diferenca muda o
      que a pessoa faz: se fui eu, respondo; se foi a Renata, falo com ela; se
      nao da para saber, procuro na equipe.

      "A equipe está atendendo" deixa de ser o padrao e passa a ser a ultima
      opcao — a resposta honesta para quando o servidor nao consegue resolver o
      nome de quem assumiu.
    */
    modeHumanYou: '👤 Você está atendendo',
    modeHumanOther: (pessoa: string) => `👤 ${pessoa} está atendendo`,
    modeHumanTeam: '👤 A equipe está atendendo',
    modeHumanFull: 'A equipe assumiu esta conversa',
    /*
      CONVERSAS WOW - "Pausado" sozinho nao diz o que esta pausado.

      O cabecalho e lido de relance; "Pausado" ao lado do nome do cliente pode
      ser entendido como o CLIENTE pausado, ou o atendimento cancelado. A frase
      nomeia quem parou.
    */
    modePaused: 'Rimati pausado',
    modePausedFull: 'O Rimati está pausado nesta conversa',
    contextTitle: 'Cliente',
    interest: 'Interesse',
    stage: 'Etapa',
    value: 'Valor estimado',
    nextStep: 'Próximo passo',
    /*
      CONVERSAS WOW - o resumo tem dono.

      "RESUMO" nao diz quem escreveu. Quem le precisa saber que aquilo foi o
      Rimati que entendeu da conversa — nao um campo que alguem preencheu.
    */
    summary: '✦ Resumo do Rimati',
    noOpportunity: 'Sem oportunidade aberta',
    /*
      R015 - nenhuma das duas frases abaixo diz "não há".

      Elas dizem o que aconteceu, e a diferença é o ponto: "sem oportunidade
      aberta" descreve o CLIENTE, "não consegui consultar" descreve o SISTEMA.
      Quando a consulta falha e a tela usa a primeira, ela afirma sobre o
      cliente uma coisa que não apurou.

      Sem sugerir recarregar: o painel refaz a consulta sozinho, e pedir uma
      ação que o produto já está tomando faz quem lê procurar um botão que não
      existe.
    */
    opportunityErro: 'Não consegui consultar as oportunidades deste cliente.',
    activityErro: 'Não consegui consultar o histórico deste cliente.',
    unknown: 'Não informado',
    /*
      ACEITAÇÃO FINAL (secao 8) - o bloco nao e so do Rimati.

      Ele mostra o historico do CONTATO, que inclui o que a equipe fez a mao:
      assumir, devolver, editar a oportunidade. Chamar isso de "Atividade do
      Rimati" e errado no titulo e, pior, empresta ao produto o credito de
      trabalho que foi de gente. Dentro da lista o ✦ continua marcando so o que
      foi automatico.

      No Inicio o painel se chama "Rimati trabalhando" e continua correto: la a
      consulta pede ao servidor apenas `agent,system`.
    */
    activity: 'Histórico recente',
    /*
      CONVERSAS WOW - o botao diz o que vai acontecer.

      "Assumir" e "Devolver" sao verbos sem objeto: assumir o que, devolver para
      quem. Sao acoes que mudam quem fala com o cliente — a pessoa precisa saber
      o efeito ANTES de clicar, nao depois.

      O nome acessivel e sempre a frase inteira; abaixo de 640px o rotulo visivel
      encolhe porque a linha tem tres controles. Quem usa leitor de tela ouve a
      mesma coisa em qualquer largura.
    */
    takeover: 'Assumir atendimento',
    takeoverShort: 'Assumir',
    release: 'Devolver ao Rimati',
    releaseShort: 'Devolver',
    /*
      ACEITAÇÃO FINAL (secao 7) - "Pausar" sozinho nao diz o que para.

      Ao lado de "Assumir atendimento", "Pausar" pode ser lido como pausar a
      CONVERSA — parar de atender o cliente. O que ele faz e calar o Rimati e
      deixar a conversa parada esperando alguem.

      A frase inteira aparece onde cabe, pela mesma consulta de container que
      decide o rotulo do "Assumir"; abaixo disso fica a curta, e o nome
      acessivel continua sendo a frase inteira em qualquer largura.
    */
    pause: 'Pausar Rimati',
    pauseShort: 'Pausar',
    takeoverHint: 'Assumir a conversa e responder pela equipe',
    releaseHint: 'Devolver a conversa ao Rimati',
    pauseHint: 'Pausar as respostas automáticas do Rimati',
    takenOver: 'Você assumiu esta conversa.',
    released: (assistente: string) => `${assistente} voltou a atender esta conversa.`,
    paused: 'O Rimati foi pausado nesta conversa.',
    needsTeam: 'Precisa de você',
    needsTeamHint: 'Esta conversa está esperando uma pessoa.',
    /** CONVERSAS WOW - a faixa de destaque do estado critico (secao 8). */
    needsTeamCalled: (assistente: string) =>
      `${assistente} chamou a equipe para continuar este atendimento.`,
    needsTeamReason: 'Motivo:',
    needsTeamSummary: 'Contexto preparado pelo Rimati:',
    /*
      CONVERSAS WOW - a falha do atendimento automatico, na lingua da recepcao.

      "Atendimento automático falhou" descreve o defeito. Quem esta com um
      cliente esperando precisa da consequencia: parou, e agora e com voce.
    */
    autoStopped: 'Atendimento automático interrompido',
    autoStoppedHint: 'Esta conversa precisa da equipe.',
    backToList: 'Voltar para a lista',
    /** RIM-183 - rotulo do botao e da folha de detalhes no celular. */
    profile: 'Informações do cliente',
    /*
      O grupo tem nome porque "Precisa de você" aparece em DOIS botoes: o filtro
      e a propria conversa na lista, cuja previa diz a mesma coisa. Sem o grupo,
      nem leitor de tela nem verificacao automatizada distinguem os dois.
    */
    filters: 'Filtrar conversas',
    filterAll: 'Todas',
    filterAttention: 'Precisa de você',
    attentionEmpty: 'Nenhuma conversa esperando você. Tudo em dia.',
    composerPlaceholder: 'Escreva para o cliente...',
    composerSend: 'Enviar',
    /*
      CONVERSAS WOW - o campo bloqueado explica QUEM esta no comando (secao 29).

      "Assuma a conversa para responder" e uma ordem sem contexto: nao diz por
      que nao da para escrever agora. Dizer que a assistente esta conduzindo
      transforma o bloqueio em informacao, e a saida fica ao lado.
    */
    composerBlocked: (assistente: string) => `✦ ${assistente} está conduzindo esta conversa`,
    /*
      R036 - o balão avisa só quando o produto NÃO SABE que chegou.

      Não há marca de "entregue" nem de "lida": o retorno da Meta ao enviar
      significa que ela aceitou, e não que o celular recebeu. Inventar um
      certinho ali seria apresentar aceite como leitura, que é justamente o
      que o critério proíbe.

      O que a equipe precisa ver é o contrário: a mensagem que não chegou, e
      a que ninguém sabe. Essas duas pedem ação; as outras não pedem nada.
    */
    deliveryFailed: 'Não chegou',
    deliveryUnknown: 'Não sabemos se chegou',
    composerTakeover: 'Assumir para responder',
    emptyThread: 'Nenhuma mensagem nesta conversa ainda.',
    /*
      CONVERSAS WOW - o painel fala de UMA oportunidade, nao da secao do menu.

      "OPORTUNIDADES" no plural e o nome da tela; aqui embaixo vem uma so, a
      desta pessoa.
    */
    opportunity: 'Oportunidade',
    /*
      CONVERSAS WOW (secao 22) - conceito e motivo estavam na mesma linha.

      "Urgência: Relata dor ou desconforto" mistura o rotulo do campo com o que
      o cliente disse. E o produto NAO calcula nivel de prioridade — inventar
      "Alta" seria numero de vitrine. "Atenção" e o que os dados sustentam.
    */
    attention: 'Atenção',
    /** CONVERSAS WOW - marcadores da lista (secao 10). */
    listAi: (assistente: string) => `${assistente} está atendendo`,
    listHuman: 'A equipe está atendendo',
    /*
      CONVERSAS WOW (secao 24) - o historico do painel, sem repetir o nome.

      Estas frases moram dentro do painel DA PESSOA: o nome dela ja esta no topo
      da tela, e repeti-lo em cada linha vira eco. As frases do Inicio nomeiam o
      cliente porque la o feed cruza varias pessoas.
    */
    panelContact: 'Rimati cadastrou este cliente',
    panelConversation: 'Rimati começou a atender',
    panelInterest: 'Rimati identificou o que o cliente procura',
    panelOpportunity: 'Rimati criou a oportunidade',
    panelStage: (etapa: string) => `Rimati avançou a oportunidade para ${etapa}`,
    panelAppointment: 'Rimati marcou o atendimento',
    panelFollowUp: 'Rimati programou um acompanhamento',
    panelHandoff: 'Rimati chamou a equipe',
    panelWon: 'Rimati registrou o fechamento',
    composerDisabled: 'Responder pela equipe entra na próxima etapa do produto.',
  },

  /*
    ASSISTENTES WOW - a conversa que ensina a assistente.

    A linguagem inteira desta tela evita o vocabulario de configuracao de IA:
    nao ha prompt, modelo, treinamento nem parametro. O que existe e alguem
    explicando como a empresa trabalha, e a assistente propondo o que entendeu.
  */
  setup: {
    title: 'Ensinar a assistente',
    /*
      Secao 27 - a diferenca entre ENSINAR e TESTAR precisa estar escrita.

      Duas telas com um campo de texto e a mesma assistente sao faceis de
      confundir, e confundi-las significa achar que se esta simulando quando na
      verdade se esta configurando.
    */
    subtitle: 'Ensine como sua empresa trabalha. Nada muda sem você aprovar.',
    cta: 'Ensinar a assistente',
    ctaHint: 'Ensine como sua empresa trabalha conversando com ela.',
    keepTeaching: 'Continuar ensinando',
    back: 'Voltar para a assistente',
    placeholder: 'Conte como vocês preferem atender...',
    send: 'Enviar',
    thinking: 'Entendendo o que você disse...',
    emptyTitle: 'Converse com a assistente sobre como sua empresa trabalha.',
    emptyHint:
      'Ela pode aprender regras, informações e preferências — sempre com a sua confirmação.',
    gapsTitle: 'Assuntos que ela ainda não conhece',
    nothingMissing: 'A assistente já conhece o essencial para atender.',
    /*
      Secao 39 - a primeira tela nao pode ser um campo em branco.

      Quando faltam assuntos, os proprios assuntos viram os atalhos. Quando nao
      falta nenhum — que e o caso da clinica demo — a pessoa ainda precisa saber
      QUE TIPO de coisa da para ensinar. Sao exemplos que ela edita antes de
      enviar, e nao frases prontas que alguem aprova sem ler.
    */
    examplesTitle: 'Exemplos do que dá para ensinar',
    examples: [
      'Nunca confirme uma condição comercial sem consultar a equipe antes.',
      'Aceitamos pix e parcelamos em até 10 vezes no cartão.',
      'Quando alguém quiser desmarcar em cima da hora, ofereça remarcar na mesma semana.',
    ],
    /** Secao 30 - o cartao de proposta, e o que cada botao faz. */
    proposalRule: 'Nova orientação sugerida',
    proposalKnowledge: 'Nova informação sugerida',
    approve: 'Aprovar',
    edit: 'Editar',
    ignore: 'Ignorar',
    /*
      Secao 8 - o recibo diz QUAL das duas coisas foi salva.

      Um "Orientação salva" embaixo de "Aceitamos pix" desfaz na hora a
      distincao que a tela acabou de ensinar: informacao e o que ela CONTA ao
      cliente, orientacao e o que ela SEGUE. Se o recibo chama as duas do mesmo
      jeito, a pessoa nao tem por que acreditar que vao para lugares diferentes.
    */
    savedRule: 'Orientação salva',
    savedRuleHint: 'Vou seguir isso nos próximos atendimentos.',
    savedKnowledge: 'Informação salva',
    savedKnowledgeHint: 'Vou usar isso quando o cliente perguntar.',
    ignored: 'Sugestão descartada',
    /*
      Secao 21 - ja existe algo parecido, e a palavra tem de bater com o que
      esta sendo aprovado.

      "Ja existe uma ORIENTACAO parecida" embaixo de um cartao que anuncia uma
      INFORMACAO desfaz, na linha seguinte, a distincao que a tela acabou de
      ensinar.
    */
    conflictTitleRule: 'Já existe uma orientação parecida',
    conflictTitleKnowledge: 'Já existe uma informação parecida',
    /*
      Duas escolhas nomeadas, e nao uma caixa de marcar.

      "Substituir a orientacao atual" numa caixinha nao diz o que acontece se
      ela ficar DESmarcada — e o que acontece e as duas passarem a valer ao
      mesmo tempo, que e uma decisao, nao um padrao silencioso. Com os dois
      botoes lado a lado, a pessoa le as duas consequencias antes de aprovar.
    */
    conflictCurrent: 'Atual',
    conflictNew: 'Nova',
    conflictReplace: 'Substituir a atual',
    conflictKeepBoth: 'Manter as duas',
    /** Secoes 10 e 11 - assuntos, e nao porcentagem inventada. */
    knownTitle: 'O que a assistente já conhece',
    gapsCount: (quantos: number) =>
      quantos === 1
        ? '1 assunto importante ainda não definido'
        : `${quantos} assuntos importantes ainda não definidos`,
    allKnown: 'A assistente já conhece o essencial para atender.',
  },

  /**
   * R074 - o catálogo da empresa.
   *
   * As frases dizem o EFEITO, e não a operação: "sai do que o Rimati oferece"
   * responde a pergunta que quem clica está fazendo — o que muda para o cliente
   * —, enquanto "serviço desativado" descreve o banco.
   */
  /**
   * R074 - as etapas do funil.
   *
   * A frase sobre não apagar fica NA TELA, e não só no código: um controle
   * ausente sem explicação é lido como esquecimento, e alguém acaba pedindo o
   * botão que a decisão recusou de propósito.
   */
  etapas: {
    titulo: 'As etapas do funil',
    hint: 'Como a empresa chama cada passo até fechar. É o que aparece no quadro de Oportunidades.',
    nomeDaEtapa: (posicao: number): string => `Nome da etapa ${posicao}`,
    subir: (nome: string): string => `Mover "${nome}" para cima`,
    descer: (nome: string): string => `Mover "${nome}" para baixo`,
    salvar: 'Salvar etapas',
    salvo: 'As etapas foram salvas. O quadro de Oportunidades já está com elas.',
    porQueNaoApaga:
      'Dá para renomear e reordenar. Apagar não: as oportunidades que estão numa etapa ficariam sem lugar, e para onde elas vão é decisão de quem toca o funil.',
  },

  catalogo: {
    servicosTitulo: 'O que a empresa faz',
    servicosHint:
      'É daqui que sai o que o Rimati oferece ao cliente, e a duração define os horários que cabem na agenda.',
    pessoasTitulo: 'Quem atende',
    pessoasHint: 'Quem pode receber um atendimento, e como o nome aparece para o cliente.',
    nomeDoServico: 'Serviço',
    nomeDoServicoDica: 'Instalação, manutenção, avaliação...',
    duracaoEmMinutos: 'Duração (min)',
    duracao: (minutos: number): string => `${minutos} minutos na agenda`,
    adicionarServico: 'Adicionar serviço',
    /*
      "Nome de quem atende", pelo mesmo motivo do botão logo abaixo.

      O formulário da equipe, nesta mesma tela, também tem um campo "Nome" - e
      ele é o nome de um usuário do sistema, não de quem atende consultas. Dois
      campos com rótulo idêntico e sentidos diferentes na mesma página são
      ambíguos para quem lê e inutilizáveis para quem navega por rótulo.
    */
    nomeDaPessoa: 'Nome de quem atende',
    nomeDaPessoaDica: 'Renata Aguiar',
    comoAparece: 'Como aparece para o cliente',
    comoApareceDica: 'Renata, da equipe (opcional)',
    apareceComo: (nome: string): string => `Aparece como "${nome}"`,
    /*
      "Adicionar quem atende", e não "Adicionar pessoa".

      A equipe já tem um botão "Adicionar pessoa" nesta MESMA tela, e ele faz
      outra coisa: cria um usuário do sistema. Dois botões com o mesmo nome e
      efeitos diferentes na mesma página confundem quem lê e são ambíguos para
      quem usa leitor de tela - a pessoa ouve o mesmo rótulo duas vezes sem nada
      que os separe.

      Descoberto pelo teste, que acusou dois casamentos para o mesmo nome.
    */
    adicionarPessoa: 'Adicionar quem atende',
    desativado: 'Desativado',
    /*
      O vazio diz a CONSEQUÊNCIA, e não o estado do banco.

      "Nenhum serviço cadastrado" descreve a tabela; o que a pessoa precisa saber
      é o que isso causa - a assistente não tem o que responder quando alguém
      pergunta se a empresa faz determinado tratamento.
    */
    semServicos: 'Catálogo de agendamento ainda vazio',
    semServicosDica:
      'O conhecimento da assistente pode descrever serviços. Cadastre aqui os serviços e suas durações para organizar a agenda.',
    semPessoas: 'Ninguém cadastrado para atender',
    semPessoasDica:
      'A agenda funciona sem isto, e o cliente não fica sabendo com quem vai ser atendido.',
    /*
      DESATIVAR, e nunca apagar. O que saiu do cardápio continua em consulta já
      marcada e no histórico de quem a fez; apagar arrastaria o passado junto.
    */
    desativar: 'Desativar',
    reativar: 'Reativar',
    servicoCriado: (nome: string): string => `"${nome}" entrou no que o Rimati oferece.`,
    servicoDesativado: (nome: string): string =>
      `"${nome}" saiu do que o Rimati oferece. Os atendimentos já marcados continuam.`,
    servicoReativado: (nome: string): string => `"${nome}" voltou a ser oferecido.`,
    profissionalCriado: (nome: string): string => `${nome} passou a poder receber atendimentos.`,
    profissionalDesativado: (nome: string): string =>
      `${nome} saiu da agenda. Os atendimentos já marcados continuam.`,
    profissionalReativado: (nome: string): string => `${nome} voltou para a agenda.`,
  },

  contacts: {
    title: 'Clientes',
    subtitle: 'Todas as pessoas que falaram com a sua empresa.',
    search: 'Buscar por nome, telefone ou e-mail',
    empty: 'Nenhum contato encontrado.',
    emptyHint: 'Ajuste a busca ou envie uma mensagem pelo simulador para criar o primeiro contato.',
    /*
      CONTATOS WOW (secao 21) - vazio de busca e vazio de verdade sao coisas
      diferentes.

      "Ajuste a busca" nao serve para quem acabou de entrar e ainda nao tem
      cliente nenhum: nao ha busca para ajustar, e a frase sugere que a pessoa
      fez algo errado. Quando a lista esta vazia sem filtro, o que informa e
      dizer de onde os contatos VEM.
    */
    noneYet: 'Nenhum contato ainda.',
    noneYetHint: 'Quando alguém falar com a sua empresa, aparece aqui.',
    /*
      R102 - a lista diz quanto dela está na tela.

      "Mostrando 50 de 72" em vez de "50 contatos": o segundo número é o que
      transforma uma lista truncada em silêncio numa lista com continuação
      declarada. Sem ele, quem procura alguém e não acha conclui que a pessoa
      não está cadastrada.
    */
    showing: (quantos: number, total: number): string =>
      `Mostrando ${quantos} de ${total} contatos`,
    loadMore: 'Carregar mais',
    status: {
      // CONVERSAS WOW (secao 18) - jargao em ingles nao diz nada a quem atende.
      lead: 'Interessado',
      active: 'Em atendimento',
      customer: 'Cliente',
      inactive: 'Inativo',
    },
    lastInteraction: 'Última interação',
    opportunities: 'Oportunidades',
    openConversation: 'Abrir a conversa em que isso aconteceu',
    /*
      CONTATOS WOW (secao 15) - o mesmo titulo que Conversas usa.

      O bloco lista o historico do contato, que inclui o que a equipe fez a mao.
      "Recente" porque ele mostra os ultimos eventos, com o resto atras de um
      botao — e nao porque alguem escolheu a palavra por soar melhor.
    */
    timeline: 'Histórico recente',
    timelineEmpty: 'Nada registrado para este contato ainda.',
    /** CONTATOS WOW (secao 16) - o resto do historico, sem sair da tela. */
    showAllHistory: (quantos: number) => `Ver tudo (${quantos})`,
    showLessHistory: 'Ver menos',
    /** O rotulo diz PARA ONDE volta; "Voltar" sozinho nao diz. */
    backToList: 'Voltar para Clientes',
    /*
      CONTATOS WOW (secao 5) - o que ocupa a direita enquanto ninguem foi
      escolhido.

      Sem isto a lista esticava para os 1144px do container e o conteudo de cada
      linha ficava colado na esquerda, com dois tercos de cartao vazio ao lado —
      e ainda dava um salto de layout ao selecionar, quando ela encolhia para
      340. E o mesmo desenho que Conversas ja usa, entao a pessoa reconhece.
    */
    pickOne: 'Escolha um contato',
    pickOneHint: 'Selecione alguém na lista para ver a situação, o próximo passo e o histórico.',
    /** CONTATOS WOW (secao 22) - os blocos do detalhe. */
    detailNextStep: 'Próximo passo',
    detailSummary: '✦ Resumo do Rimati',
    detailInfo: 'Informações',
    detailOpportunity: 'Oportunidade',
    /*
      RIM-183: era "Informações do cliente", que descreve o painel inteiro em
      vez do link. No celular o painel virou uma folha com esse mesmo titulo, e
      a duplicata ficou obvia — mas o rotulo ja era vago no desktop. O link leva
      ao cadastro; agora ele diz isso.
    */
    /** RIM-182/184 - no lugar de quatro linhas "Nao informado". */
    stillLearning: 'Ainda estamos conhecendo este cliente.',
    /** RIM-184 - rotulo curto na linha da lista. */
    nextStep: 'Próximo passo:',
    profile: 'Ver cadastro completo',
    /** CONVERSAS WOW (secao 25) - o painel mostra as ultimas; o resto fica aqui. */
    fullHistory: 'Ver histórico completo',
    edit: 'Editar contato',
    save: 'Salvar',
    cancel: 'Cancelar',
    fields: {
      procedure: 'Interesse',
      urgency: 'Urgência',
      insurance: 'Condição de atendimento',
      preferredPeriod: 'Período preferido',
      phone: 'Telefone',
      email: 'E-mail',
      name: 'Nome',
    },
  },

  opportunities: {
    title: 'Oportunidades',
    subtitle: 'Cada negociação e o próximo passo dela.',
    empty: 'Nenhuma oportunidade nesta etapa.',
    boardEmpty: 'Nenhuma oportunidade ainda.',
    boardEmptyHint: 'Quando o Rimati identificar um interesse comercial, o cartão aparece aqui.',
    noNextStep: 'Sem próximo passo definido',
    moveTo: 'Mover para',
    /** RIM-157 - edicao do cartao. */
    edit: 'Editar',
    editTitle: 'Nome da oportunidade',
    editValue: 'Valor estimado',
    editValueHint: 'Deixe em branco se ainda não há valor.',
    /** CONVERSAS WOW - "--" com peso de destaque nao e informacao. */
    noValue: 'Sem valor estimado',
    editSave: 'Salvar',
    editCancel: 'Cancelar',
    /** RIM-186 - nome acessivel do seletor de estagio no telefone. */
    stageSelector: 'Etapa do funil',
    /** RIM-180 - o filtro que o Radar do Início manda para cá. */
    filtered: (categoria: string) => `Mostrando: ${categoria}`,
    filteredEmpty: 'Nenhuma oportunidade nesta categoria agora.',
    /*
      OPORTUNIDADES WOW (secoes 2 e 28) - "em potencial", e a palavra importa.

      Oportunidade aberta nao e receita: e o que PODE entrar se cada uma andar.
      O cabecalho de uma tela de funil e o lugar mais facil de confundir as duas
      coisas, e quem confunde uma vez para de acreditar no numero.
    */
    headerSummary: (quantas: number, valor: string) =>
      quantas === 1
        ? `1 oportunidade aberta · ${valor} em potencial`
        : `${quantas} oportunidades abertas · ${valor} em potencial`,
    /*
      OPORTUNIDADES WOW (secoes 3 e 19) - o filtro que separa o que precisa
      andar.

      "Precisa de atenção" nao e uma definicao nova: e a mesma regra que carimba
      o alerta de cada cartao e alimenta o Radar do Inicio, calculada uma vez no
      servidor (`opportunity.alert.ts`). Aqui so se filtra pelo carimbo.
    */
    filterAll: 'Todas',
    filterAttention: 'Precisa de atenção',
    attentionSummary: (quantas: number, valor: string) =>
      quantas === 1
        ? `1 oportunidade · ${valor} precisa de atenção`
        : `${quantas} oportunidades · ${valor} precisam de atenção`,
    attentionNone: 'Nada parado. Todas as oportunidades têm próximo passo em dia.',
    /** Secao 27 - o vazio de uma etapa muda de sentido com o filtro ligado. */
    emptyAttention: 'Nenhuma oportunidade desta etapa precisa de atenção.',
    /*
      Secao 11 - o proximo passo e acao FUTURA, e o rotulo diz isso.

      Sem o prefixo, "Priorizar avaliação por relato de dor" se le como
      descricao do que ja aconteceu. Duas palavras resolvem, e sem cor nem
      icone: o cartao ja tem uma cor reservada para o que esta errado.
    */
    nextStepPrefix: 'Próximo:',
    /** Secao 16 - a confirmacao diz o resultado, e nao "pronto". */
    movedTo: (quem: string, etapa: string) => `${quem} movido para ${etapa}.`,
    /*
      Secao 21 - o resumo da etapa no celular.

      A terceira linha so aparece quando ha problema: um "0 precisam de atenção"
      fixo seria ruido em toda etapa saudavel.
    */
    stageSummary: (valor: string, quantas: number) =>
      quantas === 1 ? `${valor} · 1 oportunidade` : `${valor} · ${quantas} oportunidades`,
    stageAttention: (quantas: number) =>
      quantas === 1 ? '1 precisa de atenção' : `${quantas} precisam de atenção`,
    /** Secao 14 - o tempo do cartao, com rotulo em vez de solto. */
    updatedAt: 'Atualizado',
    total: 'Total',
  },

  calendar: {
    subtitle: 'Quem vem à empresa e quando.',
    /*
      RIM-187 - o topo conta o periodo (secao 11.6).

      Cada pedaco so aparece quando existe. "0 horarios livres" numa clinica que
      nunca cadastrou horario de atendimento seria lido como agenda lotada, e e
      o contrario: nao da para saber.

      AGENDA WOW (secao 2) - a frase diz de QUANDO esta falando.

      "9 consultas" sozinho nao responde "nesta semana?", "hoje?", "sempre?" — e
      quem le o topo de uma agenda esta perguntando exatamente isso.
    */
    summaryAppointments: (quantas: number, periodo: string) =>
      quantas === 1 ? `1 atendimento ${periodo}` : `${quantas} atendimentos ${periodo}`,
    periodWeek: 'esta semana',
    periodDay: 'neste dia',
    /*
      AGENDA WOW (secao 3) - so aparece quando a autoria e comprovavel.

      Ela e gravada na criacao a partir de quem chamou o servico, e nao deduzida
      de "tem conversa associada". Sem o dado, a linha nao existe.
    */
    summaryByAgent: (quantas: number) =>
      quantas === 1 ? '✦ 1 marcada pelo Rimati' : `✦ ${quantas} marcadas pelo Rimati`,
    /*
      AGENDA WOW (secao 2) - "disponíveis", e nao "livres".

      "Livre" descreve o horario como ocioso; "disponivel" descreve como
      capacidade que ainda da para vender. E a mesma contagem — a diferenca esta
      no que a palavra faz quem le pensar.
    */
    summaryFree: (quantos: number) =>
      quantos === 1 ? '1 horário disponível' : `${quantos} horários disponíveis`,
    summaryEmpty: 'Nenhum atendimento neste período.',
    today: 'Hoje',
    week: 'Semana',
    /*
      AGENDA WOW (secoes 12 e 13) - "Atual" nao dizia atual o que.

      Ele dividia a barra com o botao "Hoje", que e VISAO, e os dois pareciam a
      mesma familia. Agora o rotulo nomeia o destino, e muda com a visao: voltar
      para esta semana ou para hoje, conforme o que se esta olhando.
    */
    backToWeek: 'Esta semana',
    backToToday: 'Hoje',
    viewLabel: 'Visualização',
    navLabel: 'Período',
    previous: 'Período anterior',
    next: 'Próximo período',
    empty: 'Nenhum atendimento neste período.',
    emptyHint: 'Quando um horário for marcado, ele aparece aqui automaticamente.',
    /*
      AGENDA WOW (secao 30) - "consulta" e a palavra da recepcao.

      "Atendimento" e mais abstrato e convive com o "atendimento automatico" das
      Conversas, que e outra coisa completamente.
    */
    dayAppointments: (quantas: number) =>
      quantas === 1 ? '1 atendimento' : `${quantas} atendimentos`,
    dayFree: (quantos: number) =>
      quantos === 1 ? '1 horário disponível' : `${quantos} horários disponíveis`,
    /** Secao 31 - dia sem consulta, mas com espaco. */
    dayNoneWithSlots: 'Nenhum atendimento',
    dayClosed: 'Empresa fechada',
    /** Secao 9 - a linha do horario vago na visao Hoje. */
    slotFree: 'Disponível',
    /** Secao 10 - o resumo do dia. */
    nextAppointment: 'Próximo atendimento',
    nextFreeSlot: 'Próximo horário disponível',
    /** Secao 4 - a marca de autoria automatica na linha da consulta. */
    byAgentTag: (assistente: string) => `Marcado por ${assistente}`,
    status: {
      scheduled: 'Marcado',
      completed: 'Atendido',
      cancelled: 'Cancelado',
      no_show: 'Não veio',
    },
  },

  followups: {
    /** HOME WOW - o cabecalho diz o valor, e o numero muda todo dia. */
    valueLine: (quantos: number) =>
      quantos === 1
        ? '1 cliente será acompanhado automaticamente.'
        : `${quantos} clientes serão acompanhados automaticamente.`,
    title: 'Acompanhamentos programados',
    subtitle: 'Mensagens que o Rimati vai enviar sozinho.',
    empty: 'Nenhum acompanhamento programado.',
    emptyHint: 'Quando um cliente precisar de acompanhamento, o retorno aparece aqui.',
    cancel: 'Cancelar',
    sendNow: 'Enviar agora',
    cancelled: 'Acompanhamento cancelado.',
    sent: 'Mensagem enviada ao cliente.',
    confirmCancel: 'Cancelar este acompanhamento? O cliente não receberá a mensagem.',
    confirmSend: 'Enviar esta mensagem ao cliente agora?',
  },

  automations: {
    subtitle: 'Mensagens que o Rimati envia sozinho, sem alguém precisar lembrar.',
    on: 'Ligada',
    off: 'Desligada',
    turnOn: 'Ligar',
    turnOff: 'Desligar',
    turnedOn: 'Automação ligada.',
    turnedOff: 'Automação desligada.',
    edit: 'Editar mensagens',
    delay: 'Prazo (horas)',
    /*
      RIM-189 - a jornada em palavras (secao 11.8).

      O formulario mostrava "Prazo (horas): 24". Correto e mudo: nao conta o que
      acontece, nem em que ordem. A jornada le como uma frase — "espera 24h,
      manda a primeira; se nao responder, espera mais 48h e manda a segunda".

      O SEGUNDO PRAZO E RELATIVO NA LEITURA e absoluto no dado. O banco guarda
      72h desde o gatilho; a tela diz "+48h", porque e assim que alguem pensa
      numa sequencia. Mostrar 72 faria parecer que sao 72 horas depois da
      primeira mensagem.
    */
    journeyTrigger: 'Quando isso acontece',
    /*
      AUTOMACOES WOW (secoes 7 e 30) - o prazo diz de QUE espera se trata.

      "Espera 1 dia" e verdade nas tres automacoes e nao explica nenhuma: um dia
      sem o cliente responder, um dia antes da consulta e um dia com o orcamento
      parado sao tres regras diferentes. A frase generica obrigava quem le a
      adivinhar qual delas esta na frente.

      Cada texto abaixo reflete o que o codigo faz de verdade — conferido em
      `followup.triggers.ts` antes de escrever:

      - retomada: o prazo conta do momento em que o cliente parou de responder;
      - orcamento: conta de quando a oportunidade entrou em Orcamento;
      - lembrete: e ANTECEDENCIA, subtraida do horario da consulta.
    */
    timingNoReplyFirst: (horas: number) => `${horasEmTexto(horas)} sem resposta`,
    timingNoReplyNext: (horas: number) => `Se ainda não responder, mais ${horasEmTexto(horas)}`,
    timingStalled: (horas: number) => `${horasEmTexto(horas)} sem avançar`,
    timingBeforeAppointment: (horas: number) => `${horasEmTexto(horas)} antes do atendimento`,
    journeyStops: 'Para automaticamente quando o cliente responder.',
    journeyStopsNever: 'O lembrete continua programado mesmo que o cliente volte a conversar.',
    journeyStopsStage: 'Para automaticamente se a oportunidade avançar.',
    /*
      AUTOMACOES WOW (secoes 9 e 10) - a previa fala com o cliente.

      A tela mostrava o texto com `{nome}` e `{interesse}` a vista. Correto para
      quem edita, e e a primeira coisa que quem NAO edita ve — e sintaxe de
      marcador transforma "o Rimati escreve para seus clientes" em "voce esta
      configurando um robo". Os marcadores continuam inteiros no editor.
    */
    previewNote: 'Nome e interesse são personalizados automaticamente.',
    resultsTitle: 'Nos últimos 90 dias',
    resultsSent: (n: number) => (n === 1 ? '1 mensagem enviada' : `${n} mensagens enviadas`),
    resultsReminders: (n: number) => (n === 1 ? '1 lembrete enviado' : `${n} lembretes enviados`),
    /*
      FINAL POLISH (secao 17) - a frase diz a janela que o codigo usa.

      "Responderam depois" e verdade e nao diz quando: o servico compartilhado
      conta resposta em ate sete dias do envio, e a tela precisa afirmar
      exatamente isso. Uma frase mais larga que a regra e uma promessa que o
      numero nao cumpre.

      Continua sem afirmar causa — "responderam", e nao "voltaram por causa da
      mensagem".
    */
    resultsAnswered: (n: number) =>
      n === 1 ? '1 respondeu em até 7 dias' : `${n} responderam em até 7 dias`,
    /*
      Secoes 13 e 15 - resultado comercial so com atribuicao provada.

      As duas frases dizem "depois", e nao "gerada pela automacao": o que os
      registros provam e a ordem dos acontecimentos — a mensagem saiu, e depois a
      consulta foi marcada. Chamar isso de "consultas geradas" seria afirmar uma
      causa que ninguem mediu.
    */
    resultsAppointments: (n: number) =>
      n === 1
        ? '1 marcou atendimento depois do acompanhamento'
        : `${n} marcaram atendimento depois do acompanhamento`,
    resultsAdvanced: (n: number) =>
      n === 1 ? '1 oportunidade avançou depois' : `${n} oportunidades avançaram depois`,
    /* Secao 14 - o que ja aconteceu e o que ainda vai acontecer nao se somam. */
    upcomingTitle: 'Próximos',
    upcomingCount: (n: number) =>
      n === 1 ? '1 mensagem programada' : `${n} mensagens programadas`,
    resultsNone: 'Ainda não enviou nenhuma mensagem.',
    editMessages: 'Editar mensagens',
    message: 'Mensagem',
    placeholders:
      'Use {nome} para o primeiro nome do cliente e {interesse} para o que ele procura.',
    /* Secao 2 - o resumo operacional, so com o que os dados sustentam. */
    summaryActive: (n: number) => (n === 1 ? '1 automação ligada' : `${n} automações ligadas`),
    summaryScheduled: (n: number) =>
      n === 1
        ? 'O Rimati vai acompanhar 1 cliente automaticamente.'
        : `O Rimati vai acompanhar ${n} clientes automaticamente.`,
    summaryNothingScheduled: 'Nenhum acompanhamento programado no momento.',
    summaryAllOff: 'Nenhuma automação ligada: ninguém está sendo acompanhado automaticamente.',
    /* Secao 20 - no celular o cartao abre e fecha. */
    expand: 'Ver como funciona',
    collapse: 'Fechar',
    /* Secao 25 - desligada, o historico fica; o que para e o que viria. */
    offNotice: 'Desligada: o Rimati não vai programar novos acompanhamentos desta automação.',
  },

  /*
    CONFIGURAR COM RIMATI - a conversa que configura a operacao.

    A promessa em uma frase: ninguem precisa saber em qual tela cada
    configuracao mora. E a condicao para essa promessa nao virar um risco: nada
    e gravado por interpretacao — tudo vira proposta com o estado atual ao lado.
  */
  configSetup: {
    cta: 'Configurar com Rimati',
    ctaHint:
      'Conte como sua empresa trabalha. O Rimati prepara as mudanças e pede sua aprovação antes de aplicar.',
    title: 'Configurar com Rimati',
    subtitle: 'Explique como a empresa funciona. Nada muda sem você aprovar.',
    opening:
      'Posso te ajudar a configurar sua empresa conversando. Já considerei tudo o que está cadastrado — vou perguntar só o que ainda falta.',
    placeholder: 'Conte como sua empresa trabalha...',
    send: 'Enviar',
    thinking: 'Organizando o que você disse...',
    /*
      SECAO 11 - o estado de saude em linguagem simples.

      "Configurado" e "verificado" sao afirmacoes diferentes, e a tela dizia a
      segunda tendo apenas a primeira. Cada rotulo abaixo promete exatamente o
      que o dado sustenta.
    */
    areaState: {
      ready: 'Pronto',
      configured: 'Configurado',
      verified: 'Verificado',
      simulated: 'Só no simulador',
      incomplete: 'Falta informação',
      external: 'Depende de fora',
      failing: 'Com falha',
    } as Record<string, string>,
    mapTitle: 'O que já está configurado',
    /*
      FINAL POLISH (secoes 24 e 54) - a cor nao pode ser a unica explicacao.

      Sete selos, seis verdes e um cinza: quem olha rapido conta sete e nao sabe
      por que um esta apagado. A frase diz quantas estao prontas, quantas faltam
      informacao e quantas dependem de alguem de fora — que sao tres estados
      diferentes e nao dois.
    */
    mapSummary: (prontas: number, faltando: number, externas: number) => {
      const partes = [`${prontas} áreas configuradas`];
      if (faltando > 0)
        partes.push(`${faltando} ${faltando === 1 ? 'precisa' : 'precisam'} de informação`);
      if (externas > 0)
        partes.push(`${externas} ${externas === 1 ? 'precisa' : 'precisam'} de você`);
      return partes.join(' · ');
    },
    externalTitle: 'Ainda precisa de você',
    examplesTitle: 'Exemplos do que você pode dizer',
    examples: [
      'A empresa abre de segunda a sexta das 8 às 18 e sábado até meio-dia.',
      'Aceitamos Pix e parcelamos em 10 vezes.',
      'Se um orçamento ficar parado 2 dias, tente falar novamente.',
    ],
    /* Secao 14 - o cartao diz de que tipo de mudanca se trata. */
    kinds: {
      rule: 'Nova orientação de atendimento',
      knowledge: 'Nova informação da empresa',
      schedule: 'Horário de atendimento',
      blocked_period: 'Período sem atendimento',
      automation: 'Acompanhamento automático',
      company: 'Dados da empresa',
      appointment_move: 'Remarcação de atendimento',
      opportunity_update: 'Atualização de oportunidade',
      preference: 'Preferência pessoal',
      appointment_notice: 'Aviso ao cliente',
    },
    before: 'Atual',
    after: 'Nova',
    conflictTitle: 'Isso entra em conflito com o que já está valendo',
    replace: 'Substituir a atual',
    keepBoth: 'Manter as duas',
    applied: (titulo: string) => `${titulo} atualizado`,
    pending: (quantas: number) =>
      quantas === 1 ? '1 aguardando revisão' : `${quantas} aguardando revisão`,
    /* Secao 53 - quem volta precisa saber que deixou coisa pendente. */
    resume: (quantas: number) =>
      quantas === 1
        ? 'Você tem 1 mudança aguardando revisão.'
        : `Você tem ${quantas} mudanças aguardando revisão.`,
  },

  playground: {
    /*
      "Ele" falando da Livia.

      As frases eram genericas — servem para qualquer assistente — e por isso
      usavam o masculino. So que a tela inteira ao redor tem nome, rosto e
      historia de UMA pessoa digital, e ler "veja o que ele responde" logo
      abaixo de "Livia" e um tropeco que o olho pega na hora.

      A saida nao e adivinhar genero por nome: e falar do PAPEL. "A assistente"
      concorda sozinho, valha o assistente que valer.
    */
    title: 'Testar o atendimento',
    help: 'Escreva como um cliente escreveria e veja o que a assistente responde e o que ela faria.',
    empty: 'Escreva a primeira mensagem abaixo, como um cliente escreveria.',
    placeholder: 'Quero saber mais sobre um produto ou serviço.',
    send: 'Enviar',
    reset: 'Recomeçar',
    actions: 'O que a assistente faria',
    actionsEmpty: 'As ações aparecem aqui depois da primeira mensagem.',
    actionsNone: 'Nenhuma ação nesta resposta: foi só conversa.',
    notSaved: 'não gravado',
  },

  knowledge: {
    title: 'Informações da empresa',
    subtitle: 'O que o assistente pode afirmar. Fora disso, ele diz que não sabe.',
    empty: 'Nenhuma informação cadastrada ainda.',
    editedIn: 'Estas informações são editadas em',
  },

  agents: {
    /*
      RIM-188 - as quatro abas da secao 11.7.

      Os nomes falam do TRABALHO dela, nao da configuracao: "Como atende" e o
      que a recepcionista faria diferente de outra, e "Testar" e onde se
      experimenta sem consequencia. Nenhum diz "prompt" ou "modelo".
    */
    tabOverview: 'Visão geral',
    tabHowItWorks: 'Como atende',
    tabKnowledge: 'Conhecimento',
    tabTest: 'Testar como cliente',
    todayTitle: 'Hoje',
    /* Concordancia: "1 consultas marcadas" e um erro que o olho pega na hora. */
    todayConversations: (n: number) => (n === 1 ? 'conversa' : 'conversas'),
    todayOpportunities: (n: number) => (n === 1 ? 'oportunidade' : 'oportunidades'),
    todayAppointments: (n: number) => (n === 1 ? 'atendimento marcado' : 'atendimentos marcados'),
    capabilities: 'O que ela sabe fazer',
    testTitle: 'Modo de teste',
    /*
      ASSISTENTES WOW (secoes 26 e 28) - o aviso diz as DUAS coisas que o teste
      nao faz.

      "Nao mexe no CRM" ja estava certo e nao basta: as duas telas tem um campo
      de texto e a mesma assistente do outro lado, e quem escreve aqui pode
      achar que esta ensinando. Simular um cliente nao muda como ela atende — a
      frase abaixo diz onde se muda.
    */
    testWarning:
      'Esta conversa é apenas um teste: não cadastra clientes, não marca atendimentos e não altera as orientações da assistente.',
    testTeach: 'Para mudar como ela atende, ensine a assistente.',
    title: 'Assistentes',
    subtitle:
      'Prepare os assistentes: defina como devem atender, o que podem fazer e quando chamar sua equipe.',
    objective: 'Objetivo',
    personality: 'Estilo de atendimento',
    instructions: 'Orientações da empresa',
    /* Secao 15 - de onde vem cada coisa desta aba, sem falar de prompt. */
    howItWorksHint:
      'É assim que ela atende hoje. Cada orientação veio de uma conversa aprovada por você ou da tela de configuração.',
    noInstructions: 'Nenhuma orientação específica ainda — ela segue o estilo acima.',
    actions: 'Ações disponíveis',
    active: 'Ativo',
    inactive: 'Pausado',
    empty: 'Nenhum assistente configurado.',
    edit: 'Configurar',
    /* O painel diz de QUEM e a configuracao: "Configurar" sozinho, numa gaveta
       por cima da pagina, nao responde sobre o que ele fala. */
    editTitle: (nome: string) => `Configurar ${nome}`,
    preferTalking: 'Prefere ensinar conversando?',
    saved: 'Assistente atualizado. Vale na próxima mensagem.',
    nameLabel: 'Nome',
    roleLabel: 'Função',
    instructionsHint: 'Uma regra por linha. É o que a empresa exige do atendimento.',
    requiredTool: 'Obrigatória',
    requiredToolHint: 'É como um cliente com urgência chega até uma pessoa.',
  },

  team: {
    title: 'Equipe',
    help: 'Adicione funcionários, escolha seus perfis de acesso e bloqueie contas quando necessário.',
    invite: 'Adicionar pessoa',
    name: 'Nome',
    email: 'E-mail',
    role: 'Perfil de acesso',
    add: 'Adicionar',
    you: 'você',
    disable: 'Desativar',
    enable: 'Reativar',
    cannotDisableSelf: 'Você não pode desativar a própria conta.',
    copy: 'Copiar',
    copied: 'Senha copiada.',
    done: 'Pronto',
    created: (name: string) => `${name} foi adicionada à equipe.`,
    passwordOnce:
      'Esta senha aparece uma única vez. Passe para a pessoa: ela entra com este e-mail e troca a senha depois.',
    roles: {
      owner: 'Proprietário',
      admin: 'Administrador',
      agent: 'Atendente',
      manager: 'Gerente',
      sales: 'Equipe de vendas',
      finance: 'Equipe financeira',
      inventory: 'Responsável pelo estoque',
      editor: 'Responsável pela loja online',
      technician: 'Técnico de campo',
      delivery: 'Responsável pelas entregas',
      accountant: 'Responsável pela contabilidade',
      sales_inventory: 'Vendas e estoque',
    },
    rolesHelp: 'O perfil determina quais ações a pessoa poderá realizar nas ferramentas ativas.',
    roleAccess: {
      owner: 'Gestão completa da empresa.',
      admin: 'Gestão completa, menos mudar o proprietário.',
      agent: 'Atende clientes e vende no PDV; não mexe em estoque nem em finanças.',
      manager: 'Vende, acompanha estoque e decide exceções; não vê contas a receber.',
      sales: 'Vende no PDV da equipe; não vê produtos, estoque nem custos.',
      finance: 'Cuida de recebíveis e rotinas financeiras; não vende nem mexe em estoque.',
      inventory: 'Faz contagens e consulta perdas; não vende nem vê finanças.',
      editor: 'Cuida da loja online e das campanhas; sem estoque nem finanças.',
      technician: 'Vê só os próprios serviços de campo.',
      delivery: 'Vê só as próprias entregas.',
      accountant: 'Consulta documentos contábeis autorizados; não vende nem mexe em estoque.',
      sales_inventory: 'Vende no PDV e cuida do estoque; sem custos, lucro nem finanças.',
    },
    noScreenWarning:
      "Com as ferramentas de hoje, essa pessoa não verá nenhuma tela. Ative o PDV da equipe ou escolha 'Vendas e estoque'.",
    prepareCounter: 'Preparar o PDV da equipe',
    noScreensHome: 'Ainda não há telas liberadas para você. Fale com o responsável pela loja.',
  },

  health: {
    /*
      RIM-191 - "Saude do atendimento" em linguagem de quem opera (secao 11.10).

      Cada linha responde "isto esta funcionando?" sem citar provedor, saldo ou
      erro bruto. Quando algo esta errado, o texto diz o que fazer — nao o
      codigo do erro.
    */
    title: 'Saúde do atendimento',
    hint: 'O que precisa estar funcionando para o Rimati atender sozinho.',
    whatsapp: 'WhatsApp',
    whatsappOk: 'Conectado e recebendo mensagens.',
    whatsappOff: 'Nenhum canal conectado. O Rimati não recebe mensagens.',
    agent: 'Assistente',
    agentOk: 'Atendendo normalmente.',
    agentOff: 'Desligada. As conversas ficam esperando alguém da equipe.',
    agentFailing: (quantas: number) =>
      quantas === 1
        ? '1 atendimento falhou nas últimas 48 horas.'
        : `${quantas} atendimentos falharam nas últimas 48 horas.`,
    calendar: 'Agenda',
    calendarOk: 'Horário de atendimento cadastrado.',
    calendarOff: 'Sem agenda cadastrada. Necessária apenas se sua empresa oferece agendamentos.',
    statusOk: 'Tudo certo',
    statusAttention: 'Precisa de atenção',
  },

  diagnostics: {
    title: 'Falhas do atendimento automático · 24h',
    help: 'Falhas das conversas com clientes. A conversa de gestão com o Rimati não está incluída.',
    refresh: 'Atualizar',
    executions: 'Atendimentos (24h)',
    failures: 'Falharam',
    latency: 'Tempo de resposta',
    model: 'Modelo em uso',
    /*
      RIM-191 - o detalhe tecnico fica ATRAS de um clique (secao 11.10).

      "rimati-dental-rules-v1" nao diz nada para quem administra uma clinica, e
      ainda assim ocupava um quarto da linha de indicadores. Quem precisa dele
      esta investigando; para essa pessoa, um clique nao e obstaculo.
    */
    technical: 'Ver detalhes técnicos',
    technicalHide: 'Ocultar detalhes técnicos',
    empty: 'Nenhuma falha nas últimas 24 horas.',
    open: 'Abrir a conversa',
    openWith: (name: string) => `Abrir a conversa com ${name}`,
    detail: 'Detalhe técnico',
  },

  schedule: {
    title: 'Horário de atendimento',
    help: 'É daqui que saem os horários que o Rimati oferece ao cliente.',
    to: 'até',
    each: 'cada',
    saved: 'Horário salvo. Vale para os próximos horários oferecidos.',
    keepsAppointments:
      'Mudar o horário não desmarca atendimento já agendado: ele continua na Agenda.',
    blocks: 'Períodos bloqueados',
    blocksHelp: 'Feriado, evento, férias. Nenhum horário é oferecido dentro deles.',
    blocksEmpty: 'Nenhum período bloqueado.',
    blockFrom: 'De',
    blockTo: 'Até',
    blockReason: 'Motivo',
    blockReasonPlaceholder: 'Evento da equipe',
    blockAdd: 'Bloquear',
    blockRemove: 'Remover',
    blockCreated: 'Período bloqueado.',
    blockRemoved: 'Bloqueio removido.',
  },

  settings: {
    subtitle:
      'Atualize os dados da empresa, conecte os canais de atendimento e revise as informações usadas pelo Rimati.',
    company: 'Empresa',
    companyEdit: 'Editar',
    companySaved: 'Dados da empresa atualizados.',
    timezoneHelp:
      'O fuso horário decide os horários que o Rimati oferece ao cliente. Use o formato America/Bahia.',
    companyName: 'Nome',
    timezone: 'Fuso horário',
    currency: 'Moeda',
    channels: 'Canais',
    channelsHelp: 'Por onde as mensagens chegam e saem.',
    connected: 'Conectado',
    notConnected: 'Não conectado',
    simulatorHint: 'Conversa de teste para conferir o atendimento sem enviar mensagens a clientes',
    noNumber: 'Nenhum número conectado ainda',
    connectedNoNumber: 'Número conectado, sem apelido cadastrado',
    knowledge: 'Informações da empresa',
    knowledgeHelp:
      'É exatamente o que o Rimati pode afirmar. O que não estiver aqui, ele diz que não sabe.',
    /* Secoes 23 e 58 - a secao comeca fechada e diz o que tem dentro. */
    knowledgeCount: (n: number) =>
      n === 1 ? '1 informação cadastrada' : `${n} informações cadastradas`,
    knowledgeShow: 'Ver informações',
    knowledgeHide: 'Fechar',
    knowledgeSearch: 'Buscar por assunto ou texto...',
    knowledgeAdd: 'Nova informação',
    knowledgeTitle: 'Assunto',
    knowledgeCategory: 'Categoria',
    knowledgeContent: 'O que o Rimati pode dizer',
    knowledgeKeywords: 'Como o cliente costuma perguntar',
    knowledgeKeywordsHint: 'Separe por vírgula. Ajuda o Rimati a encontrar esta informação.',
    knowledgeEdit: 'Editar',
    knowledgeDisable: 'Desativar',
    knowledgeEnable: 'Ativar',
    knowledgeOn: 'Informação ativada.',
    knowledgeOff: 'Informação desativada. O Rimati deixa de usá-la.',
  },

  simulator: {
    name: 'Nome do cliente',
    phone: 'Telefone',
    message: 'Mensagem',
    send: 'Enviar como cliente',
    sending: 'Enviando...',
    sent: 'Mensagem recebida pelo Rimati.',
    reset: 'Restaurar ambiente demo',
    resetting: 'Restaurando...',
    resetDone: 'Ambiente demo restaurado.',
    resetConfirm: 'Isso apaga contatos, conversas e oportunidades e recria os dados iniciais.',
    examples: 'Exemplos',
    openConversation: 'Abrir a conversa',
  },

  demo: {
    /*
      RIM-193 - o fechamento da demonstracao.

      Repare no que a frase NAO diz: nada de "economizou X horas" ou "recuperou
      R$ Y". Nao temos como provar nenhum dos dois — nao sabemos quanto tempo a
      recepcao levaria, e oportunidade criada nao e receita. Um numero que o
      cliente nao consegue verificar destroi a confianca nos que ele consegue.
    */
    summaryTitle: 'O que o Rimati acabou de fazer',
    /* R105 - sumir nao e um estado: 'nada aconteceu' e 'nao consegui perguntar' pedem reacoes opostas. */
    summaryErro: 'Não consegui consultar o que o Rimati fez agora há pouco.',
    /*
      ACEITAÇÃO FINAL (secao 3) - "sem ninguem digitar" e uma afirmacao, e
      precisa de prova.

      O tempo sempre foi medido: da primeira a ultima acao da janela. A segunda
      metade da frase, nao. O componente pedia ao servidor so as acoes de
      `agent,system` — o que EXCLUI o que a equipe fez, e nao verifica que ela
      nao fez nada. Se alguem tivesse assumido uma conversa e respondido no
      meio da janela, a frase continuaria dizendo que ninguem digitou.

      Agora sao duas frases. A longa so aparece quando a janela nao tem UMA
      acao humana registrada — assumir, devolver, pausar, editar oportunidade,
      marcar consulta a mao. Nao havendo prova, o resumo diz apenas o que
      mediu.
    */
    summaryHint: (minutos: number, semHumano: boolean) => {
      const tempo = minutos === 1 ? 'Tudo isso em 1 minuto' : `Tudo isso em ${minutos} minutos`;
      return semHumano ? `${tempo}, sem ninguém digitar.` : `${tempo}.`;
    },
  },

  notifications: {
    /*
      RIM-165 - o texto do aviso do navegador.

      Ele aparece na tela de bloqueio e no canto do monitor da recepcao, a vista
      de quem passa. Por isso nao traz o texto da mensagem, nem interesse nem
      urgencia: so o primeiro nome (no titulo) e o fato de que alguem espera.
    */
    handoffBody: 'Pediu para falar com a equipe.',
    title: 'Avisos no navegador',
    hint: 'Receba um aviso quando alguém pedir atendimento, mesmo com a aba em segundo plano.',
    enable: 'Ativar avisos',
    disable: 'Desativar avisos',
    blocked: 'Os avisos estão bloqueados nas configurações do navegador.',
    unsupported: 'Este navegador não mostra avisos.',
    onHere: 'Ativado neste computador.',
  },

  /*
    R059 - relatar um problema.

    O texto da ressalva de privacidade fica JUNTO do botão de enviar, e não em
    letra miúda: quem relata da tela de Conversas tem razão em achar que a
    conversa vai junto, porque é o que a maioria dos produtos faz. Dizer que
    não vai é o que permite a pessoa escrever o que for necessário para a
    queixa ser entendida.
  */
  support: {
    report: 'Relatar um problema',
    whatHappened: 'O que aconteceu?',
    originHint: (origem: string) => `Vamos registrar que foi em ${origem}.`,
    placeholder: 'Ex.: a agenda fica carregando e não abre, só pelo celular.',
    attach: 'Anexar imagem',
    attachFailed: 'Não conseguimos anexar a imagem.',
    sendFailed: 'Não conseguimos enviar o relato agora.',
    send: 'Enviar',
    received: 'Recebemos seu relato.',
    receivedHint: 'Você acompanha o andamento em Configurações.',
    privacyNote:
      'Enviamos a página, o navegador e o tamanho da tela. A conversa aberta NÃO vai junto — se ela for importante, conte aqui o que aconteceu nela.',
  },

  common: {
    loading: 'Carregando...',
    remove: 'Remover',
    retry: 'Tentar novamente',
    errorTitle: 'Não conseguimos carregar esta tela',
    errorHint: 'Verifique sua conexão e tente novamente.',
    offline: 'Recarregue para ver novidades',
    offlineHint:
      'A conexão que avisa esta tela sobre mudanças feitas em outra aba ou por outra pessoa está indisponível. Seu acesso, seus dados e o assistente podem continuar funcionando. Recarregue a página para buscar novidades.',
    online: 'Novidades aparecem sozinhas',
    onlineHint:
      'Mudanças feitas em outra aba ou por outra pessoa aparecem nesta tela sem precisar recarregar.',
    logout: 'Sair',
    close: 'Fechar',
    back: 'Voltar',
    all: 'Todos',
    open: 'Abrir',
    saved: 'Alterações salvas.',
    genericError: 'Algo deu errado. Tente novamente.',
  },
} as const;

export const CONTACT_STATUS_LABEL = t.contacts.status;

/** Texto único sobre operar sem internet (PDV e página de ajuda). Não prometer venda offline. */
export const SEM_INTERNET_TRANSPARENCIA =
  'Sem internet, o PDV não funciona e nenhuma venda é concluída: vendas exigem conexão. O que fica preservado é o carrinho, que continua salvo neste navegador até a conexão voltar.';
